import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

export function PhotoGallery({ photos, columns = 3 }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const openLightbox = (index) => {
    setSelectedIndex(index);
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setIsOpen(false);
    setSelectedIndex(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = useCallback(() => {
    setSelectedIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  }, [photos.length]);

  const goToNext = useCallback(() => {
    setSelectedIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  }, [photos.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goToPrevious, goToNext]);

  return (
    <>
      {/* Thumbnail Grid */}
      <div 
        className={`grid gap-4 ${
          columns === 2 ? 'grid-cols-2' : 
          columns === 3 ? 'grid-cols-2 md:grid-cols-3' : 
          'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
        }`}
        data-testid="photo-gallery-grid"
      >
        {photos.map((photo, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.05 }}
            className="group relative aspect-square overflow-hidden rounded-xl cursor-pointer bg-muted"
            onClick={() => openLightbox(index)}
            data-testid={`gallery-thumbnail-${index}`}
          >
            <img
              src={photo.url}
              alt={photo.caption || `Photo ${index + 1}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
              <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            {photo.caption && (
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-white text-sm font-medium truncate">{photo.caption}</p>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Lightbox Slider - rendered via Portal to ensure it's above everything */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isOpen && selectedIndex !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[9999] bg-black flex flex-col items-center justify-center"
              style={{ position: 'fixed', margin: 0, padding: 0 }}
              onClick={closeLightbox}
              data-testid="lightbox-overlay"
            >
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-[10000] p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="lightbox-close"
              >
                <X className="w-6 h-6 text-white" />
              </button>

              {/* Previous Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-[10000] p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>

              {/* Image Container */}
              <motion.div
                key={selectedIndex}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col items-center justify-center px-16 py-4"
                style={{ maxWidth: '90vw', maxHeight: 'calc(100vh - 120px)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={photos[selectedIndex].url}
                  alt={photos[selectedIndex].caption || `Photo ${selectedIndex + 1}`}
                  className="object-contain rounded-lg"
                  style={{ maxWidth: '85vw', maxHeight: 'calc(100vh - 180px)' }}
                />
                {photos[selectedIndex].caption && (
                  <p className="mt-3 text-white text-center text-lg">
                    {photos[selectedIndex].caption}
                  </p>
                )}
                <p className="mt-1 text-white/60 text-sm">
                  {selectedIndex + 1} / {photos.length}
                </p>
              </motion.div>

              {/* Next Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-[10000] p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>

              {/* Thumbnail Strip */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto p-2 bg-black/50 rounded-lg z-[10000]">
                {photos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedIndex(index);
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      index === selectedIndex ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={photo.url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
